import { Injectable } from "@angular/core";
import { Observable, Subject } from 'rxjs';
import { map, tap } from "rxjs/operators";
import { AskCommentDetailResponse, AskCommentResponse, AskDetail, AskDetailPaginatedList, AskDetailResponse } from 'src/app/interfaces/ask';
import { MBTI } from 'src/app/interfaces/mbti';
import { CustomHttpClient } from 'src/app/services/http/http.service';
import { SnackbarService } from 'src/app/services/snackbar/snackbar.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: "root"
})
export class AskService {
  constructor(private http: CustomHttpClient, private snackbarService: SnackbarService) {

  }
  
  // 최상단에 올 ask detail
  topAskPostDetail: AskDetail | undefined;
  
  // 애스크 홈에 노출 여부
  private _isAskVisibleOnHome = true;
  private isAskVisibleOnHomeSubject = new Subject<boolean>();
  private isAskVisibleOnMbtiResultSubject = new Subject<boolean>();
  private askCreateDismissSubject = new Subject<void>()
  private askPostCreateSubject = new Subject<AskDetail>()
  private askPostDeleteSubject = new Subject<number>()
  private commentDeleteSubject = new Subject<number>()

  get isAskVisibleOnHome(): boolean {
    return this._isAskVisibleOnHome;
  }

  getIsAskVisibleOnHomeSubject(): Observable<boolean> {
    return this.isAskVisibleOnHomeSubject.asObservable();
  }
  getIsAskVisibleOnMbtiResultSubject(): Observable<boolean> {
    return this.isAskVisibleOnMbtiResultSubject.asObservable();
  }

  isAskVisibleOnHomeChanged(value: boolean): void {
    this._isAskVisibleOnHome = value;
    this.isAskVisibleOnHomeSubject.next(value);
  }

  /** 이상하게 모달 그냥 닫으면 select option이 뜨는 문제 때문에 강제로 닫아주기 위함; */
  getAskCreateDismissSubject(): Observable<void> {
    return this.askCreateDismissSubject.asObservable();
  }

  dismissAskCreateModal(): void {
    this.askCreateDismissSubject.next();
  }

  getAskPostCreateSubject(): Observable<AskDetail> {
    return this.askPostCreateSubject.asObservable();
  }

  getAskPostDeleteSubject(): Observable<number> {
    return this.askPostDeleteSubject.asObservable();
  }

  getCommentDeleteSubject(): Observable<number> {
    return this.commentDeleteSubject.asObservable();
  }

  getAskDetailList(mbtiType?: MBTI, cursor?: string | null, ordering?: 'comment_count' | 'like_count', pageSize?: number, ): Observable<AskDetailPaginatedList> {
    const queryParams: { mbtiType?: MBTI; cursor?: string | null; ordering?: 'comment_count' | 'like_count';  pageSize?: number} = { mbtiType, cursor, ordering, pageSize };
    return this.http.get<AskDetailPaginatedList>(`${environment.apiUrl}/v1/communities/ask/posts`, { queryParams })
      .pipe(map(res => {
      const newData = res.data.map((item) => ({
        ...item,
        comments: item.comments.sort((a, b) => {
          if (a.createdAt >= b.createdAt) return 1;
          else return -1;
        })
      }));
      return {
        ...res,
        data: newData
      }
    }))
  }

  /** ask 게시물, 댓글 삭제 */
  deletePost(postId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/v1/communities/ask/posts/${postId}`)
      .pipe(tap(() => {
        this.askPostDeleteSubject.next(postId);
        this.snackbarService.openSnackBar(SnackbarService.ASK_POST_DELETED);
      }))
  }

  /** ask 게시물, 댓글 신고 */
  reportPost(postId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/v1/communities/ask/posts/${postId}/report`)
      .pipe((tap(() => this.snackbarService.openSnackBar(SnackbarService.ASK_REPORT_SUBMITTED))));
  }

  createPost(mbtiType: MBTI, content: string): Observable<AskDetailResponse> {
    return this.http.post<AskDetailResponse>(`${environment.apiUrl}/v1/communities/ask/posts`, {
      content,
      mentionMbtiType: mbtiType
    }).pipe(tap((res) => {
      this.askPostCreateSubject.next(res.data);
      this.snackbarService.openSnackBar(SnackbarService.ASK_POST_CREATED);
    }));
  }

  updatePost(postId: number, content: string): Observable<void> {
    return this.http.patch(`${environment.apiUrl}/v1/communities/ask/posts/${postId}`, {
      content
    });
  }

  /** 포스트 좋아요 */
  likePost(postId: number): Observable<void> {
    return this.http.post(`${environment.apiUrl}/v1/communities/ask/posts/${postId}/like`);
  }

  /** 댓글 생성 */
  createComment(postId: number, content: string): Observable<AskCommentResponse> {
    return this.http.post<AskCommentResponse>(`${environment.apiUrl}/v1/communities/ask/posts/${postId}/comments`, { content })
      .pipe((tap(() => this.snackbarService.openSnackBar(SnackbarService.ASK_COMMENT_CREATED))));
  }  

  /** 댓글 업데이트 */

  updateComment(commentId: number, content: string): Observable<void> {
    return this.http.patch(`${environment.apiUrl}/v1/communities/ask/comments/${commentId}`, { content });
  }

  /** 댓글 삭제  */
  deleteComment(commentId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/v1/communities/ask/comments/${commentId}`).pipe(tap(() => {
      this.commentDeleteSubject.next(commentId);
      this.snackbarService.openSnackBar(SnackbarService.ASK_COMMENT_DELETED);
    }))
  }

  /** 댓글 좋아요 */
  likeComment(commentId: number): Observable<void> {
    return this.http.post(`${environment.apiUrl}/v1/communities/ask/comments/${commentId}/like`);
  }

  reportComment(commentId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/v1/communities/ask/comments/${commentId}/report`)
      .pipe((tap(() => this.snackbarService.openSnackBar(SnackbarService.ASK_REPORT_SUBMITTED))));
  }

  /** get single post */
  getAskDetail(postId: number): Observable<AskDetailResponse> {
    return this.http.get<AskDetailResponse>(`${environment.apiUrl}/v1/communities/ask/posts/${postId}`);
  }

  /** get single comment */
  getCommentDetail(commentId: number): Observable<AskCommentDetailResponse> {
    return this.http.get<AskCommentDetailResponse>(`${environment.apiUrl}/v1/communities/ask/comments/${commentId}`);
  }
}